import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import filtersStore, {FrameType} from './filters-store';
import storePageDataSource from '../../storepage-datasource';
import {Portal} from '../../../util';
import {PriceRangeSlider} from '../price-range/price-range-slider';

const frameTypes = [
    {label: 'Tassel Frame', value: FrameType.Tassel},
    {label: 'Diploma Frame', value: FrameType.Diploma},
    {label: 'Certificate Frame', value: FrameType.Certificate},
];

const closeFilters = () => {
    const menu = document.getElementById('store-filters-container') as HTMLElement;

    if (menu) {
        menu.style.display = 'none';
        menu.classList.remove('filters-visible');
    }
};

export const Filters: React.FC = observer(() => {
    const isBestSellersPage = window.location.pathname.includes('best-sellers');

    const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        filtersStore.updateFilter('sort', e.target.value);
    }, []);

    const handleSchoolSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        filtersStore.onSchoolSearch(e.target.value);
    }, []);

    return (
        <div
            id="store-filters-container"
            role="complementary"
            aria-label="Product filters"
        >
            <div id="store-filters-x">
                <button
                    type="button"
                    onClick={closeFilters}
                    aria-label="Close Filter Menu"
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                    }}
                >
                    <i
                        className="fas fa-times"
                        aria-hidden="true"
                    />
                </button>
            </div>

            <div id="sort-by-container">
                <label htmlFor="sort-by">
                    <strong>Sort by</strong>
                </label>
                <select
                    aria-label="Sort By"
                    name="sort-by"
                    id="sort-by"
                    className="form-select d-inline-block"
                    onChange={handleSortChange}
                    value={filtersStore.filters.sort}
                >
                    <option value="0">Featured</option>
                    <option value="1">Price: Low to High</option>
                    <option value="2">Price: High to Low</option>
                    <option value="3">Best Selling</option>
                    {Portal?.PortalID !== 3 && <option value="4">Best Reviews</option>}
                    {Portal?.PortalID !== 3 && <option value="5">Fast Shipping</option>}
                </select>
            </div>

            <hr />

            <div id="price-range-container">
                <PriceRangeSlider />
            </div>

            <hr />

            <div id="search-school-container">
                <label htmlFor="school-search">
                    {Portal && Portal.PortalID < 1 && <strong>Search for your school</strong>}
                    {Portal?.PortalID === 1 && <strong>Search for your organization</strong>}
                    {Portal?.PortalID === 2 && <strong>Search for your Military Force</strong>}
                    {Portal?.PortalID === 3 && <strong>Search for your school/state</strong>}
                </label>

                <input
                    id="school-search"
                    className="form-control d-inline-block"
                    onChange={handleSchoolSearch}
                    aria-label={`Search for your ${Portal && Portal.PortalID < 1 ? 'school' : Portal?.PortalID === 1 ? 'organization' : Portal?.PortalID === 2 ? 'Military Force' : 'school'}`}
                />

                <div id="store-filters-apply">
                    <button
                        type="button"
                        onClick={closeFilters}
                        aria-label="Apply filters and close filter menu"
                    >
                        Apply Filter
                    </button>
                </div>
            </div>

            <hr />

            <div id="mouldings-container">
                <div>
                    <strong>Moulding Options</strong>
                </div>
                <div className="filters-scrollable">
                    {storePageDataSource.mouldingOptions.map(moulding => (
                        <label key={moulding}>
                            <input
                                className="filter-moulding"
                                type="checkbox"
                                value={moulding}
                                checked={filtersStore.selectedFinish.includes(moulding)}
                                onChange={e => filtersStore.finishChanged(e.target.checked, e.target.value)}
                            />
                            {moulding || 'All Mouldings'}
                        </label>
                    ))}
                </div>
            </div>

            <hr />

            {!isBestSellersPage && (
                <div id="mats-container">
                    <div>
                        <strong>Mat Options</strong>
                    </div>
                    <div className="filters-scrollable">
                        {storePageDataSource.matOptions.map(mat => (
                            <label key={mat}>
                                <input
                                    className="filter-mat"
                                    type="checkbox"
                                    value={mat}
                                    checked={filtersStore.selectedMatCombo.includes(mat)}
                                    onChange={e => filtersStore.matComboChanged(e.target.checked, e.target.value)}
                                />
                                {mat || 'All Mats'}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {Portal?.PortalID === 3 && (
                <>
                    <hr />
                    <div id="frame-type-container">
                        <div>
                            <strong>Frame Type</strong>
                        </div>
                        <div className="filters-scrollable">
                            {frameTypes.map(frame => (
                                <label key={frame.value}>
                                    <input
                                        type="checkbox"
                                        value={frame.value}
                                        checked={filtersStore.selectedFrameType.includes(frame.value)}
                                        onChange={e => filtersStore.frameTypeChanged(e.target.checked, e.target.value as FrameType)}
                                    />
                                    {frame.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <hr />
                    <div id="graduation-year-container">
                        <label htmlFor="graduation-year-search">
                            <strong>Graduation Year</strong>
                        </label>
                        <input
                            id="graduation-year-search"
                            className="form-control d-inline-block"
                            placeholder="Enter graduation year"
                            type="number"
                            value={filtersStore.selectedGraduationYear}
                            onChange={e => filtersStore.graduationYearChanged(e.target.value)}
                            aria-label="Enter graduation year"
                        />
                        {filtersStore.graduationYearError && (
                            <div
                                className="text-danger mt-1"
                                style={{fontSize: 13}}
                            >
                                {filtersStore.graduationYearError}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
});
